import { PurchaseActionOption, PurchaseItemStatus } from "common";
import React, { useState } from "react";
import { ExperiencePurchaseItem, MerchandisePurchaseItem, Purchase, PurchaseItem } from "types";
import { useVisibility } from "../../hooks/useVisibility";
import { CreatePurchaseItemState, CreatePurchaseState } from "../../stores/createPurchaseStore";
import { RefundOption } from "../Dashboard/RefundOption";
import { PurchaseChangeQuantityModal } from "./ChangeQuantity/PurchaseChangeQuantityModal";
import { CollectBalanceOption, CollectBalanceOptionsModal } from "./CollectBalanceDeciderModal";
import { CollectPurchaseBalanceViaInvoiceModal } from "./CollectBalanceViaInvoiceModal";
import { CollectPurchaseBalanceModal } from "./CollectPurchaseBalanceModal";
import { EditCustomLineItemListModal } from "./EditCustomLineItemsModal";
import { EditPurchaseCodesModal } from "./EditPurchaseCodesModal";
import { EditPurchaseTaxesAndFeesModal } from "./EditPurchaseTaxesAndFeesModal";
import { PurchaseReturnPaymentModal, ReturnPaymentSource } from "./Refund/PurchaseReturnPaymentModal";
import { UpdateMerchandiseQuantityModal } from "./UpdateQuantity/UpdateMerchandiseQuantityModal";
import { EditGratuityModal } from "@/components/PurchaseModification/EditGratuityModal";
import { UpdateFulfilledQuantityModal } from "@/components/PurchaseModification/MerchandiseFulfillment/UpdateFulfilledQuantityModal";
import { isMerchandiseItem } from "@/models/PurchaseItem";

interface ModificationModalProps {
    taxes: { isOpen: boolean; onChange: (id: string) => void };
    guests: { isOpen: boolean; onChange: (id: string) => void };
    codes: { isOpen: boolean; onChange: (id: string) => void };
    customItems: { isOpen: boolean; onChange: (id: string) => void };
    gratuity: { isOpen: boolean; onChange: (id: string) => void };
    fulfilledQuantity: { isOpen: boolean; onChange: (id: string) => void };
    merchandiseQuantity: { isOpen: boolean; lineItemId: string; onChange: (id: string) => void };
}

interface PurchaseModificationModalsProps {
    purchase: Purchase | CreatePurchaseState;
    item: PurchaseItem | CreatePurchaseItemState;
    modalState: ModificationModalProps;
}

export const PurchaseModificationModals = ({ purchase, item, modalState }: PurchaseModificationModalsProps) => {
    const itemId = item.id ?? "";
    const [isCollectBalanceOpen, { show: showCollectBalance, hide: hideCollectBalance }] = useVisibility(false);
    const [selectedOption, setSelectedOption] = useState<PurchaseActionOption | null>(null);
    const [refundAmount, setRefundAmount] = useState(0);
    const [refundItem, setRefundItem] = useState<PurchaseItem | null>(null);
    const [returnPaymentSource, setReturnPaymentSource] = useState<ReturnPaymentSource | null>(null);
    const [selectedRefundOption, setSelectedRefundOption] = useState<RefundOption | null>(null);

    const handleCloseCollectBalance = () => {
        hideCollectBalance();
        setSelectedOption(null);
    };

    const handleClosePurchaseChangeQuantityModal = (
        nextModalOption: PurchaseActionOption | null,
        nextRefundOption: RefundOption | null,
        refundAmount: number,
        _newPurchase?: Purchase,
        nextRefundItem?: PurchaseItem,
    ) => {
        modalState.guests.onChange(itemId);
        setSelectedOption(nextModalOption);
        setSelectedRefundOption(nextRefundOption);
        setRefundAmount(refundAmount);
        setRefundItem(nextRefundItem ?? null);

        setReturnPaymentSource(
            nextRefundOption === RefundOption.RETURN_PAYMENT ? ReturnPaymentSource.CHANGE_GUEST_QUANTITY : null,
        );
    };

    return (
        <>
            <EditPurchaseTaxesAndFeesModal
                purchase={purchase as Purchase}
                item={item as PurchaseItem}
                isOpen={modalState.taxes.isOpen}
                onClose={() => modalState.taxes.onChange(itemId)}
            />

            <EditPurchaseCodesModal
                purchase={purchase as Purchase}
                item={item as PurchaseItem}
                isOpen={modalState.codes.isOpen}
                onClose={() => modalState.codes.onChange(itemId)}
            />

            {"object" in purchase ? (
                <>
                    <PurchaseChangeQuantityModal
                        purchase={purchase}
                        item={item as ExperiencePurchaseItem}
                        isOpen={modalState.guests.isOpen}
                        onClose={handleClosePurchaseChangeQuantityModal}
                    />
                    {isMerchandiseItem(item) && (
                        <UpdateMerchandiseQuantityModal
                            purchase={purchase}
                            item={item as MerchandisePurchaseItem}
                            isOpen={modalState.merchandiseQuantity.isOpen}
                            onClose={(
                                nextModalOption,
                                nextRefundOption,
                                refundAmount,
                                _newPurchase,
                                nextRefundItem,
                            ) => {
                                modalState.merchandiseQuantity.onChange("");
                                setSelectedOption(nextModalOption);
                                setSelectedRefundOption(nextRefundOption);
                                setRefundAmount(refundAmount);
                                setRefundItem(nextRefundItem ?? null);
                                setReturnPaymentSource(
                                    nextRefundOption === RefundOption.RETURN_PAYMENT
                                        ? ReturnPaymentSource.CHANGE_MERCHANDISE_QUANTITY
                                        : null,
                                );
                            }}
                        />
                    )}
                    <EditCustomLineItemListModal
                        purchase={purchase}
                        item={item as ExperiencePurchaseItem}
                        isOpen={modalState.customItems.isOpen}
                        onClose={(purchase) => {
                            modalState.customItems.onChange(itemId);
                            const hasBalance = purchase?.balance && purchase.balance > 0.01;
                            if (hasBalance) {
                                showCollectBalance();
                            }
                        }}
                    />

                    <EditGratuityModal
                        purchase={purchase}
                        item={item as ExperiencePurchaseItem}
                        isOpen={modalState.gratuity.isOpen}
                        onClose={(purchase) => {
                            modalState.gratuity.onChange(itemId);
                            const hasBalance = purchase?.balance && purchase.balance > 0.01;
                            if (hasBalance) {
                                showCollectBalance();
                            }
                        }}
                    />

                    <CollectBalanceOptionsModal
                        purchase={purchase}
                        isOpen={isCollectBalanceOpen || selectedOption === PurchaseActionOption.COLLECT_BALANCE_OPTIONS}
                        onClose={handleCloseCollectBalance}
                        onConfirm={(action: CollectBalanceOption) => {
                            if (action === CollectBalanceOption.COLLECT_NOW) {
                                setSelectedOption(PurchaseActionOption.COLLECT_BALANCE);
                                hideCollectBalance();
                            } else if (action === CollectBalanceOption.COLLECT_VIA_INVOICE) {
                                setSelectedOption(PurchaseActionOption.COLLECT_BALANCE_VIA_INVOICE);
                                hideCollectBalance();
                            } else {
                                handleCloseCollectBalance();
                            }
                        }}
                    />
                    <PurchaseReturnPaymentModal
                        purchase={purchase}
                        item={(refundItem ?? item) as ExperiencePurchaseItem}
                        refundAmount={refundAmount}
                        shouldReduce={false}
                        returnPaymentSource={returnPaymentSource}
                        isOpen={selectedRefundOption === RefundOption.RETURN_PAYMENT}
                        onClose={() => {
                            setSelectedRefundOption(null);
                            setReturnPaymentSource(null);
                            setRefundAmount(0);
                            setRefundItem(null);
                        }}
                    />
                    {item.status !== PurchaseItemStatus.DRAFT && (
                        // These are for a pre-existing purchase only
                        <>
                            <CollectPurchaseBalanceModal
                                purchase={purchase}
                                isOpen={selectedOption === PurchaseActionOption.COLLECT_BALANCE}
                                onClose={() => setSelectedOption(null)}
                            />

                            <CollectPurchaseBalanceViaInvoiceModal
                                purchase={purchase}
                                item={item}
                                isOpen={selectedOption === PurchaseActionOption.COLLECT_BALANCE_VIA_INVOICE}
                                onClose={() => setSelectedOption(null)}
                            />
                        </>
                    )}

                    <UpdateFulfilledQuantityModal
                        purchase={purchase}
                        isOpen={modalState.fulfilledQuantity.isOpen}
                        onClose={() => {
                            modalState.fulfilledQuantity.onChange(itemId);
                        }}
                    />
                </>
            ) : null}
        </>
    );
};
